import { useCallback, useEffect, useState } from 'react';
/**
 * ## Partner
 * 회사 파트너 관리 페이지
 */
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Edit } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TPartnerFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import { Page } from 'src/components/Page';
import useRenderUseYnCell from 'src/hooks/renderUseYnCell';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { FormInnerContainer, FormLabel } from 'src/layouts/form/FormLayout';
import { IPartner, useStores } from 'src/models';
import { partnerApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants';

import { PartnerFormDialog } from './PartnerFormDialog';

interface SortableItemProps {
  partner: IPartner;
  onClick: () => void;
}

// 드래그 아이템 컴포넌트
const SortableItem = ({ partner, onClick }: SortableItemProps) => {
  const renderUseYnCell = useRenderUseYnCell();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: partner.partnerSid,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging) {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    }
  };

  return (
    <StyledPartnerWrapper ref={setNodeRef} style={style} {...attributes} onClick={handleClick}>
      <StyledPartnerCard>
        <StyledUseYnBadge useYn={partner.useYn}>{renderUseYnCell(partner.useYn)}</StyledUseYnBadge>
        <StyledImageContainer {...listeners}>
          <img
            src={process.env.REACT_APP_API_URL + (partner.logoImgPath || '')}
            alt={partner.partnerName || ''}
          />
        </StyledImageContainer>
      </StyledPartnerCard>
      <StyledPartnerInfo>
        <StyledPartnerCategory>{`#${partner.partnerCtgyCd?.value}`}</StyledPartnerCategory>
        <StyledInfoRow>
          <StyledPartnerName>{partner.partnerName}</StyledPartnerName>
          <StyledEditIcon>
            <Edit fontSize="small" />
          </StyledEditIcon>
        </StyledInfoRow>
      </StyledPartnerInfo>
    </StyledPartnerWrapper>
  );
};

export const Partner = () => {
  const { alertStore } = useStores();
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const { t } = useTranslation();
  const { commonCodeStore } = useStores();
  const { openDialog, closeDialog } = useDialogContext();
  const { callApiWithLoading } = useCallApiWithLoading();
  // 전체 데이터를 한 번에 관리
  const [partnerData, setPartnerData] = useState<IPartner[]>([]);
  // 파트너 카테고리 코드 가져오기 (헬스케어, ICT)
  const partnerCategories = commonCodeStore.getCCodeByName('PARTNER_CTGY_CD');
  // 파트너 카테고리별 필터링하여 리스트 반환
  const getPartnersByCategory = useCallback(
    (categoryCode: number) => {
      const filtered = partnerData.filter((partner) => {
        return partner.partnerCtgyCd?.code === categoryCode;
      });
      return filtered;
    },
    [partnerData],
  );
  // 순서 변경 핸들러
  const handleDragEnd = (event: DragEndEvent, categoryCode: number) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // 현재 카테고리의 파트너들만 필터링
    const filteredPartners = getPartnersByCategory(categoryCode);
    // 필터링된 리스트 내에서의 인덱스 추출
    const filteredOldIndex = filteredPartners.findIndex((item) => item.partnerSid === active.id);
    // 드래그된 아이템
    const movedItem = filteredPartners[filteredOldIndex];

    // 전체 리스트에서의 인덱스
    const oldIndex = partnerData.findIndex((item) => item.partnerSid === active.id);
    const newIndex = partnerData.findIndex((item) => item.partnerSid === over.id);

    // UI 업데이트 (순서 변경된 데이터 임시 저장, alert 취소 시 원복하기 위함)
    setPartnerData((items) => arrayMove(items, oldIndex, newIndex));

    // 드래그 시작할 때 알림
    alertStore.setProps({
      open: true,
      title: '순서 변경 안내',
      content: '드래그하여 순서를 변경하면 즉시 반영됩니다.',
      category: 'info',
      hasCancelButton: true,
    });

    alertStore.setOnCancel(() => {
      setPartnerData((items) => arrayMove(items, newIndex, oldIndex)); // 취소 시 원래 데이터로 복원
    });

    // 순서 변경 버튼 클릭 시 실행될 콜백
    alertStore.setOnConfirm(async () => {
      try {
        await partnerApi.put(movedItem.partnerSid, {
          ...movedItem,
          // 현재 카테고리 코드
          partnerCtgyCd: categoryCode,
          // 해당 카테고리 내에서 순서가 아니라 전체목록에서 순서로 세팅
          ordr: newIndex + 1,
        } as TPartnerFormData);
      } catch (error) {
        // API 실패 시 원래 위치로 복구
        setPartnerData((items) => arrayMove(items, newIndex, oldIndex));
      }
    });
  };
  // 목록 조회
  const getPartnerData = useCallback(async () => {
    await partnerApi.getPartner().then((res) => {
      if (res) {
        const newGridRows = res.data.map((partner, i) => {
          return { ...partner, id: partner.partnerSid ?? i };
        });
        setPartnerData(newGridRows);
      }
    });
  }, []);

  useEffect(() => {
    getPartnerData();
  }, [getPartnerData]);
  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.PARTNER);
    getPartnerData();
  };
  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.PARTNER,
      formType: FormType.CREATE,
      maxWidth: 'md',
      dialogContent: (
        <PartnerFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 수정 dialog open
  const handleItemClick = (param: IPartner) => {
    callApiWithLoading(async () => {
      await partnerApi.getDetail(param.partnerSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.PARTNER,
            formType: FormType.UPDATE,
            maxWidth: 'md',
            dialogContent: (
              <PartnerFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };
  return (
    <Page>
      <ContentBox>
        <ContentBoxButtons>
          <ContentButtonCreate onClick={handleAdd}>
            {t('common.button.register')}
          </ContentButtonCreate>
        </ContentBoxButtons>

        {partnerCategories?.map((category) => {
          return (
            <FormInnerContainer key={category.value}>
              <FormLabel sx={{ fontSize: '19px', fontWeight: 'bold' }}>{category.label}</FormLabel>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(result) => handleDragEnd(result, category.value)}
              >
                <SortableContext
                  items={getPartnersByCategory(category.value).map((p) => p.partnerSid)}
                  strategy={rectSortingStrategy}
                >
                  <StyledPartnerGrid>
                    {getPartnersByCategory(category.value).map((partner) => (
                      <SortableItem
                        key={partner.partnerSid}
                        partner={partner}
                        onClick={() => handleItemClick(partner)}
                      />
                    ))}
                  </StyledPartnerGrid>
                </SortableContext>
              </DndContext>
            </FormInnerContainer>
          );
        })}
      </ContentBox>
    </Page>
  );
};

const StyledPartnerGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, 150px)',
  gap: '16px',
  padding: '16px',
  minHeight: '220px',
});

const StyledPartnerWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '150px',
  cursor: 'grab',
  touchAction: 'none',
  border: '1px solid #dde1e6',
  borderRadius: '8px',

  '&:active': {
    cursor: 'grabbing',
  },
});

const StyledPartnerCard = styled(Box)(({ theme }) => ({
  width: '150px',
  height: '150px',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#fff',
  position: 'relative',
  marginBottom: '8px',

  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
}));

const StyledImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f5f5f5',
  cursor: 'grab',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '5px 8px',
    backgroundColor: theme.palette.grey[500],
  },

  '&:active': {
    cursor: 'grabbing',
  },
}));

const StyledPartnerInfo = styled(Box)({
  padding: '5px 10px',
});

const StyledPartnerName = styled('div')({
  fontSize: '14px',
  fontWeight: 'bold',
  marginBottom: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const StyledInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const StyledEditIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  opacity: 0.7,
  transition: 'opacity 0.2s',

  '&:hover': {
    opacity: 1,
  },
}));

const StyledPartnerCategory = styled('div')(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  marginRight: '8px',
}));

const StyledUseYnBadge = styled('div')<{ useYn: boolean }>(({ theme, useYn }) => ({
  position: 'absolute',
  top: '3px',
  right: '3px',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 'bold',
  color: theme.palette.common.white,
  zIndex: 1,
}));

export default observer(Partner);
